// src/components/modules/patients/Patients.js

import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../../axiosConfig";
import {
  Table,
  Button,
  Input,
  Tag,
  Modal,
  Pagination,
  Spin,
  message,
  Select,
  Dropdown,
  Card,
  Typography,
  Grid,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  HistoryOutlined,
  DownloadOutlined,
  PhoneOutlined,
  IdcardOutlined,
  HomeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CarOutlined
} from "@ant-design/icons";
import EditPatientForm from "./Form/EditPatientForm";
import DeletePatientDialog from "./DeletePatientDialog";
import ViewPatientDialog from "./ViewPatientDialog";
import AddPatientForm from "./Form/AddPatientForm";
import BulkUploadPatients from "./BulkUploadPatients";
import HistorialPacienteDialog from "./HistorialPacienteDialog";
import PacientesTransferModal from "./PacientesTransferModal";
import { useUser } from "../../../UserContext";
import "./Patients.css";

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [zonas, setZonas] = useState([]);
  const [loadingZonas, setLoadingZonas] = useState(false);
  const [selectedZona, setSelectedZona] = useState(null);
  const [tieneRestriccionZonas, setTieneRestriccionZonas] = useState(false);

  const [filter, setFilter] = useState("");
  const [searchInput, setSearchInput] = useState(filter)

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [patientNameToDelete, setPatientNameToDelete] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [selectedPacienteId, setSelectedPacienteId] = useState(null);
  const [transfersModalOpen, setTransfersModalOpen] = useState(false);
  const [selectedPacienteIdForTransfers, setSelectedPacienteIdForTransfers] =useState(null);

  // Para estados
  const [estados, setEstados] = useState([]);
  const [loadingEstados, setLoadingEstados] = useState(false);
  const [selectedEstado, setSelectedEstado] = useState(null);

  // Para EPS
  const [eps, setEps] = useState([]);
  const [loadingEps, setLoadingEps] = useState(false);
  const [selectedEps, setSelectedEps] = useState(null);

  // Departamentos y Ciudades
  const [departamentos, setDepartamentos] = useState([]);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [selectedDepartamento, setSelectedDepartamento] = useState(null);

  const [ciudades, setCiudades] = useState([]);
  const [loadingCiudades, setLoadingCiudades] = useState(false);
  const [selectedCiudad, setSelectedCiudad] = useState(null);
  const { user: currentUser } = useUser();
  const screens = useBreakpoint();

  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [exportLoading, setExportLoading] = useState(false);

  const hasPermission = useCallback(
    (moduleKey, permission) => {
      // 1. Verificar si el módulo está disponible
      const module = currentUser?.modulos?.find((m) => m.key === moduleKey);
      if (!module || module.estado !== "disponible") return false;

      // 2. Verificar permisos específicos para el módulo
      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === moduleKey && mp.permiso === permission
      );
    },
    [currentUser]
  );

  // Función optimizada para obtener pacientes con paginación
  const fetchPatients = useCallback(async () => {
    setListLoading(true);
    try {
      const params = {
        page: page,
        per_page: rowsPerPage
      };
      
      if (filter) params.search = filter;
      if (selectedZona) params.zona_id = selectedZona;
      if (selectedEstado) params.estado = selectedEstado;
      if (selectedDepartamento) params.departamento_id = selectedDepartamento;
      if (selectedCiudad) params.ciudad_id = selectedCiudad;
      if (selectedEps) params.eps = selectedEps;

      const response = await api.get("/pacientes/listado-optimizado", { params });

      // La nueva API retorna datos paginados
      const paginatedData = response.data;
      
      setPatients(paginatedData.data || []);
      setTotalPatients(paginatedData.total || 0);
    } catch (error) {
      console.error("Error al obtener los pacientes:", error);
      message.error("Error al obtener los pacientes");
      setPatients([]);
      setTotalPatients(0);
    } finally {
      setListLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    filter,
    selectedZona,
    selectedEstado,
    selectedDepartamento,
    selectedCiudad,
    selectedEps,
  ]);

  // Zonas - carga solo las disponibles para el rol del usuario
  const fetchZonas = useCallback(async () => {
    setLoadingZonas(true);
    try {
      const response = await api.get("/pacientes/zonas-disponibles");
      setZonas(response.data.zonas || []);
      setTieneRestriccionZonas(response.data.tiene_restriccion || false);
    } catch (error) {
      console.error("Error al obtener las zonas:", error);
      message.error("Error al obtener las zonas");
      setZonas([]);
    } finally {
      setLoadingZonas(false);
    }
  }, []);

  // Estados
  const fetchEstados = useCallback(async () => {
    setLoadingEstados(true);
    try {
      const response = await api.get("/estados");
      setEstados(response.data);
    } catch (error) {
      console.error("Error al obtener los estados:", error);
      message.error("Error al obtener los estados");
    } finally {
      setLoadingEstados(false);
    }
  }, []);

  // Departamentos
  const fetchDepartamentos = useCallback(async () => {
    setLoadingDepartamentos(true);
    try {
      const response = await api.get("/departamentos");
      setDepartamentos(response.data);
    } catch (error) {
      console.error("Error al obtener los departamentos:", error);
      message.error("Error al obtener los departamentos");
    } finally {
      setLoadingDepartamentos(false);
    }
  }, []);

  // Ciudades (al seleccionar un departamento)
  const fetchCiudades = useCallback(async (departamento_id) => {
    if (!departamento_id) {
      setCiudades([]);
      return;
    }
    setLoadingCiudades(true);
    try {
      const response = await api.get("/ciudades", {
        params: { departamento_id },
      });
      setCiudades(response.data);
    } catch (error) {
      console.error("Error al obtener las ciudades:", error);
      message.error("Error al obtener las ciudades");
    } finally {
      setLoadingCiudades(false);
    }
  }, []);

  // Fetch EPS (filtradas por rol del usuario)
  const fetchEps = useCallback(async () => {
    setLoadingEps(true);
    try {
      // Cargar EPS filtradas por rol del usuario
      const response = await api.get("/roles/clientes/usuario");
      const { clientes, tiene_restriccion } = response.data;

      if (tiene_restriccion && clientes && clientes.length > 0) {
        // Mapear al formato esperado por el Select
        setEps(clientes.map(c => ({ id: c.id, nombre_cliente: c.nombre })));
      } else {
        // Sin restricción, cargar todos los clientes
        const allClientes = await api.get("/clientes/list");
        setEps(allClientes.data || []);
      }
    } catch (error) {
      console.error("Error al obtener las EPS:", error);
      // Fallback: cargar todos los clientes
      try {
        const fallbackResponse = await api.get("/clientes/list");
        setEps(fallbackResponse.data || []);
      } catch (fallbackError) {
        message.error("Error al obtener las EPS");
        setEps([]);
      }
    } finally {
      setLoadingEps(false);
    }
  }, []);

  useEffect(() => {
    fetchZonas();
    fetchEstados();
    fetchDepartamentos();
    fetchEps();
  }, [fetchZonas, fetchEstados, fetchDepartamentos, fetchEps]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);


  // Cargar ciudades cuando se selecciona un departamento
  useEffect(() => {
    if (selectedDepartamento) {
      fetchCiudades(selectedDepartamento);
      setSelectedCiudad(null);
    } else {
      setCiudades([]);
      setSelectedCiudad(null);
    }
  }, [selectedDepartamento, fetchCiudades]);

  /**
   * Implementa el debounce para el campo de búsqueda.
   * Espera 800 ms después de la última escritura antes de actualizar el filtro.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilter(searchInput);
    }, 800); // 800 ms de retraso

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  /**
   * Maneja el cambio en el campo de búsqueda.
   * Actualiza el estado local `searchInput`.
   */
  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (current, size) => {
    setRowsPerPage(size);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleEditClick = useCallback(async (patient) => {
    try {
      setLoadingPatient(true);
      const response = await api.get(`/pacientes/${patient.id}`);
      setSelectedPatient(response.data);
      setEditDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener el paciente:", error);
      message.error("Error al obtener los datos del paciente");
    } finally {
      setLoadingPatient(false);
    }
  }, []);

  const handleViewClick = useCallback(async (patient) => {
    try {
      setLoadingPatient(true);
      const response = await api.get(`/pacientes/${patient.id}`);
      setSelectedPatient(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error al obtener el paciente:", error);
      message.error("Error al obtener los datos del paciente");
    } finally {
      setLoadingPatient(false);
    }
  }, []);

  const handleOpenDeleteDialog = useCallback((patient) => {
    setPatientToDelete(patient.id);
    setPatientNameToDelete(patient.nombre);
    setDeleteDialogOpen(true);
  }, []);

  const handleHistorialClick = useCallback((patient) => {
    setSelectedPacienteId(patient.id);
    setHistorialDialogOpen(true);
  }, []);

const handleExport = async () => {
  setExportLoading(true);

  try {
    // Construir parámetros de filtros actuales
    const params = {
      ...(filter && { search: filter }),
      ...(selectedZona && { zona_id: selectedZona }),
      ...(selectedEstado && { estado: selectedEstado }),
      ...(selectedDepartamento && { departamento_id: selectedDepartamento }),
      ...(selectedCiudad && { ciudad_id: selectedCiudad }),
      ...(selectedEps && { eps: selectedEps }),
    };

    // Realizar la petición
    const response = await api.get('/pacientes/exportar-excel', {
      params,
      responseType: 'blob'
    });

    // Crear enlace de descarga
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pacientes_exportados_${new Date().toISOString().slice(0,10)}.xlsx`);
    document.body.appendChild(link);
    link.click();
    
    // Limpiar
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    // Cerrar modal
    setExportModalVisible(false);
  } catch (error) {
    console.error('Error exporting patients:', error);
    message.error('Error al exportar pacientes');
  } finally {
    setExportLoading(false);
  }
};


  useEffect(() => {
    setPage(1);
  }, [filter, selectedZona, selectedEstado, selectedDepartamento, selectedCiudad, selectedEps]);

  const columns = useMemo(
    () => [
      {
        title: "Paciente",
        dataIndex: "nombre",
        key: "nombre",
        width: 200,
        render: (text, patient) => (
          <div className="patient-name">{text}</div>
        ),
        sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      },
      {
        title: "Tipo de Documento",
        dataIndex: "TipoDocumento",
        key: "TipoDocumento",
        render: (TipoDocumento, record) =>
          record.tipo_documento_descripcion ||
          TipoDocumento?.descripcion ||
          "Desconocido",
        sorter: (a, b) =>
          (
            a.tipo_documento ||
            a.TipoDocumento?.descripcion ||
            ""
          ).localeCompare(
            b.tipo_documento || b.TipoDocumento?.descripcion || ""
          ),
      },
      {
        title: "Número de Documento",
        dataIndex: "numero_documento",
        key: "numero_documento",
        sorter: (a, b) =>
          (a.numero_documento || "").localeCompare(b.numero_documento || ""),
      },
      {
        title: "EPS",
        dataIndex: "eps_nombre",
        key: "eps_nombre",
        render: (eps_nombre) => eps_nombre || "Sin EPS",
        sorter: (a, b) => {
          const epsA = a.eps_nombre || "";
          const epsB = b.eps_nombre || "";
          return epsA.localeCompare(epsB);
        },
      },
      {
        title: "Zona",
        dataIndex: "zona_nombre",
        key: "zona_nombre",
        render: (zona_nombre) => zona_nombre || "Sin Zona",
        sorter: (a, b) =>
          (a.zona_nombre || "").localeCompare(b.zona_nombre || ""),
      },
      {
        title: "Ciudad, Departamento",
        key: "ciudad_departamento",
        render: (_, record) =>
          record.ciudad_nombre && record.departamento_nombre
            ? `${record.ciudad_nombre}, ${record.departamento_nombre}`
            : "Desconocido",
        sorter: (a, b) => {
          const aVal = `${a.ciudad_nombre || ""}, ${
            a.departamento_nombre || ""
          }`;
          const bVal = `${b.ciudad_nombre || ""}, ${
            b.departamento_nombre || ""
          }`;
          return aVal.localeCompare(bVal);
        },
      },
      {
        title: "Estado",
        dataIndex: "estado_descripcion",
        key: "estado_descripcion",
        width: 120,
        render: (estado) => {
          if (!estado) {
            return <Tag className="status-tag-unknown">Desconocido</Tag>;
          }

          const estadoLower = estado.toLowerCase();
          let className = "status-tag-unknown";
          
          if (estadoLower === "activo") {
            className = "status-tag-active";
          } else if (estadoLower === "suspendido") {
            className = "status-tag-suspended";
          } else if (estadoLower === "fallecido") {
            className = "status-tag-fallecido";
          } else if (estadoLower === "hospitalizado") {
            className = "status-tag-hospitalizado";
          }

          return (
            <Tag className={className}>
              {estado}
            </Tag>
          );
        },
        sorter: (a, b) =>
          (a.estado_descripcion || "").localeCompare(
            b.estado_descripcion || ""
          ),
      },
      {
        title: "Acciones",
        key: "acciones",
        render: (text, paciente) => {
          const items = [
            hasPermission("Pacientes", "VISUALIZAR_P") && {
              key: "ver",
              icon: <EyeOutlined />,
              label: "Ver Paciente",
              onClick: () => handleViewClick(paciente),
            },
            hasPermission("Pacientes", "EDITAR_P") && {
              key: "editar",
              icon: <EditOutlined />,
              label: "Editar Paciente",
              onClick: () => handleEditClick(paciente),
            },
            hasPermission("Pacientes", "ELIMINAR_P") && {
              key: "eliminar",
              icon: <DeleteOutlined />,
              label: "Eliminar Paciente",
              onClick: () => handleOpenDeleteDialog(paciente),
            },
            hasPermission("Pacientes", "VISUALIZAR_P") && {
              key: "historial",
              icon: <HistoryOutlined />,
              label: "Historial",
              onClick: () => handleHistorialClick(paciente),
            },
            hasPermission("Pacientes", "VISUALIZAR_P") && {
              key: "historial-traslados",
              icon: <CarOutlined/>,
              label: "Historial de Traslados",
              onClick: () => {
                setSelectedPacienteIdForTransfers(paciente.id);
                setTransfersModalOpen(true);
              },
            },
          ].filter(Boolean);

          return (
            <div className="patient-actions-dropdown">
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  icon={<EllipsisOutlined />}
                  className="patient-actions-btn"
                  type="text"
                />
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [
      handleViewClick,
      handleEditClick,
      handleOpenDeleteDialog,
      handleHistorialClick,
      hasPermission,
    ]
  );

  const tableHeaderStyle = {
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 1,
    boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.4)",
  };

  /**
   * Los pacientes ya vienen paginados del backend, no necesitamos paginar de nuevo.
   */

  /**
   * Renderiza una tarjeta de paciente para vista móvil
   */
  const renderPatientCard = (patient) => {
    const items = [
      hasPermission("Pacientes", "VISUALIZAR_P") && {
        key: "ver",
        icon: <EyeOutlined />,
        label: "Ver Paciente",
        onClick: () => handleViewClick(patient),
      },
      hasPermission("Pacientes", "EDITAR_P") && {
        key: "editar",
        icon: <EditOutlined />,
        label: "Editar Paciente",
        onClick: () => handleEditClick(patient),
      },
      hasPermission("Pacientes", "ELIMINAR_P") && {
        key: "eliminar",
        icon: <DeleteOutlined />,
        label: "Eliminar Paciente",
        onClick: () => handleOpenDeleteDialog(patient),
      },
      hasPermission("Pacientes", "VISUALIZAR_P") && {
        key: "historial",
        icon: <HistoryOutlined />,
        label: "Ver Historial",
        onClick: () => handleHistorialClick(patient),
      },
    ].filter(Boolean);

    return (
      <Card key={patient.id} className="patient-mobile-card">
        <div className="patient-mobile-header">
          <div className="patient-mobile-info">
            <div className="patient-mobile-name">{patient.nombre}</div>
            <div className="patient-mobile-document">
              {patient.tipo_documento_descripcion || 'Documento'}: {patient.numero_documento || 'No registrado'}
            </div>
          </div>
          <div className="patient-mobile-actions">
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                icon={<EllipsisOutlined />}
                className="patient-mobile-actions-btn"
                type="text"
              />
            </Dropdown>
          </div>
        </div>

        <div className="patient-mobile-details">
          <div className="patient-mobile-detail">
            <PhoneOutlined className="patient-mobile-detail-icon" />
            <span className="patient-mobile-detail-text">
              Teléfono: {patient.telefono || 'No registrado'}
            </span>
          </div>

          <div className="patient-mobile-detail">
            <IdcardOutlined className="patient-mobile-detail-icon" />
            <span className="patient-mobile-detail-text">
              EPS: {patient.eps_nombre || 'No registrada'}
            </span>
          </div>

          <div className="patient-mobile-detail">
            <HomeOutlined className="patient-mobile-detail-icon" />
            <span className="patient-mobile-detail-text">
              Ubicación: {patient.ciudad_nombre && patient.departamento_nombre 
                ? `${patient.ciudad_nombre}, ${patient.departamento_nombre}`
                : 'No registrada'}
            </span>
          </div>

          <div className="patient-mobile-detail">
            <span className="patient-mobile-detail-text">
              Estado: 
              <Tag
                className={(() => {
                  if (!patient.estado_descripcion) {
                    return "status-tag-unknown";
                  }
                  const estadoLower = patient.estado_descripcion.toLowerCase();
                  if (estadoLower === "activo") return "status-tag-active";
                  if (estadoLower === "suspendido") return "status-tag-suspended";
                  if (estadoLower === "fallecido") return "status-tag-fallecido";
                  if (estadoLower === "hospitalizado") return "status-tag-hospitalizado";
                  return "status-tag-unknown";
                })()}
                style={{ marginLeft: '8px' }}
              >
                {patient.estado_descripcion || 'Desconocido'}
              </Tag>
            </span>
          </div>

          <div className="patient-mobile-dates">
            <div className="patient-mobile-detail">
              <CalendarOutlined className="patient-mobile-detail-icon" />
              <span className="patient-mobile-detail-text">
                Creado: {patient.createdAt ? new Date(patient.createdAt).toLocaleDateString("es-CO") : 'No registrado'}
              </span>
            </div>
            <div className="patient-mobile-detail">
              <ClockCircleOutlined className="patient-mobile-detail-icon" />
              <span className="patient-mobile-detail-text">
                Actualizado: {patient.updatedAt ? new Date(patient.updatedAt).toLocaleDateString("es-CO") : 'No registrado'}
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  /**
   * Determina si se debe mostrar la vista móvil
   */
  const isMobile = !screens.md;

  return (
    <div className="patients-list-container">
      {/* Header Section */}
      <div className="patients-list-header">
        <Title level={2} className="patients-list-title">
          Gestión de Pacientes
        </Title>
        <Text className="patients-list-subtitle">
          Administra los pacientes del sistema
        </Text>

        <div className="patients-list-actions">
          <div className="patients-list-search">
            <Input
              placeholder="Buscar por nombre, teléfono o número de documento"
              value={searchInput}
              onChange={handleSearchChange}
              suffix={<SearchOutlined />}
              size="large"
              allowClear
            />
          </div>

          <div className="patients-action-buttons">
            {hasPermission("Pacientes", "CREAR_P") && (
              <Button
                className="patients-list-add-btn"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => setAddDialogOpen(true)}
              >
                Agregar Paciente
              </Button>
            )}
            {hasPermission("Pacientes", "CREAR_P") && (
              <Button
                className="patients-list-upload-btn"
                icon={<UploadOutlined />}
                size="large"
                onClick={() => setBulkUploadDialogOpen(true)}
              >
                Importar Pacientes
              </Button>
            )}
            {hasPermission("Pacientes", "VISUALIZAR_P") && (
              <Button
                className="patients-list-export-btn"
                icon={<DownloadOutlined />}
                size="large"
                onClick={() => setExportModalVisible(true)}
              >
                Exportar Pacientes
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="patients-list-filters">
        <div className="patients-filters-row">
          <div className="patients-filter-select">
            <Select
              placeholder="Departamento"
              allowClear
              onChange={(value) => setSelectedDepartamento(value)}
              loading={loadingDepartamentos}
              value={selectedDepartamento}
            >
              {(departamentos || []).map((dep) => (
                <Option key={dep.id} value={dep.id}>
                  {dep.nombre}
                </Option>
              ))}
            </Select>
          </div>

          <div className="patients-filter-select">
            <Select
              placeholder="Ciudad"
              allowClear
              onChange={(value) => setSelectedCiudad(value)}
              loading={loadingCiudades}
              value={selectedCiudad}
              disabled={!selectedDepartamento}
            >
              {(ciudades || []).map((ciud) => (
                <Option key={ciud.id} value={ciud.id}>
                  {ciud.nombre}
                </Option>
              ))}
            </Select>
          </div>

          <div className="patients-filter-select">
            <Select
              placeholder="EPS"
              allowClear
              onChange={(value) => setSelectedEps(value)}
              loading={loadingEps}
              value={selectedEps}
              showSearch
              optionFilterProp="children"
            >
              {(eps || []).map((ep) => (
                <Option key={ep.id} value={ep.id}>
                  {ep.nombre_cliente}
                </Option>
              ))}
            </Select>
          </div>

          <div className="patients-filter-select">
            <Select
              placeholder={tieneRestriccionZonas ? "Zona (restringido)" : "Zona"}
              allowClear
              onChange={(value) => setSelectedZona(value)}
              loading={loadingZonas}
              value={selectedZona}
              showSearch
              optionFilterProp="children"
            >
              {(zonas || []).map((zona) => (
                <Option key={zona.id} value={zona.id}>
                  {zona.nombre}
                </Option>
              ))}
            </Select>
          </div>

          <div className="patients-filter-select">
            <Select
              placeholder="Estado"
              allowClear
              onChange={(value) => setSelectedEstado(value)}
              loading={loadingEstados}
              value={selectedEstado}
            >
              {(estados || []).map((estado) => (
                <Option key={estado.id} value={estado.descripcion}>
                  {estado.descripcion}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      {/* Content Container */}
      <Spin
        spinning={listLoading}
        tip="Cargando pacientes..."
        className="patients-list-loading"
      >
        {isMobile ? (
          /* Mobile Cards View */
          <div className="patients-mobile-container">
            <div className="patients-mobile-grid">
              {patients.map((patient) => renderPatientCard(patient))}
            </div>

            {/* Mobile Pagination */}
            <div className="patients-mobile-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={totalPatients}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={handleRowsPerPageChange}
                pageSizeOptions={["5", "10", "25"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total}`
                }
                size="small"
                simple
              />
            </div>
          </div>
        ) : (
          /* Desktop Table View */
          <div className="patients-list-table-container">
            <Table
              className="patients-list-table"
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({
                  style: tableHeaderStyle,
                }),
              }))}
              dataSource={patients}
              pagination={false}
              rowKey="id"
              scroll={{ x: 1200, y: 600 }}
            />

            {/* Desktop Pagination */}
            <div className="patients-list-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={totalPatients}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={handleRowsPerPageChange}
                pageSizeOptions={["5", "10", "25", "50", "100"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total} pacientes`
                }
              />
            </div>
          </div>
        )}
      </Spin>

      {selectedPatient && (
        <EditPatientForm
          patient={selectedPatient}
          open={editDialogOpen}
          handleClose={() => setEditDialogOpen(false)}
          fetchPatients={fetchPatients}
          loading={loadingPatient}
        />
      )}

      {viewDialogOpen && (
        <ViewPatientDialog
          patient={selectedPatient}
          open={viewDialogOpen}
          handleClose={() => setViewDialogOpen(false)}
          loading={loadingPatient}
        />
      )}

      {deleteDialogOpen && (
        <DeletePatientDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          patientId={patientToDelete}
          patientName={patientNameToDelete}
          fetchPatients={fetchPatients}
        />
      )}

      <AddPatientForm
        open={addDialogOpen}
        handleClose={() => setAddDialogOpen(false)}
        fetchPatients={fetchPatients}
      />

      <Modal
        open={bulkUploadDialogOpen}
        onCancel={() => setBulkUploadDialogOpen(false)}
        footer={[
          <Button key="close" onClick={() => setBulkUploadDialogOpen(false)}>
            Cerrar
          </Button>,
        ]}
        width="60%"
      >
        <BulkUploadPatients fetchPatients={fetchPatients} />
      </Modal>

<Modal
  title="Exportar Pacientes"
  open={exportModalVisible}
  onCancel={() => setExportModalVisible(false)}
  footer={[
    <Button key="cancel" onClick={() => setExportModalVisible(false)}>
      Cancelar
    </Button>,
    <Button
      key="export"
      type="primary"
      loading={exportLoading}
      onClick={handleExport}
      icon={<DownloadOutlined />}
    >
      Exportar
    </Button>,
  ]}
>
  <div style={{ padding: "20px 0", textAlign: 'center' }}>
    <p>Se exportarán todos los pacientes con los filtros actuales aplicados.</p>
    <p style={{ color: "#1890ff", marginTop: 10 }}>
      {totalPatients} pacientes serán incluidos en el reporte
    </p>
  </div>
</Modal>

      {historialDialogOpen && (
        <HistorialPacienteDialog
          open={historialDialogOpen}
          onClose={() => setHistorialDialogOpen(false)}
          pacienteId={selectedPacienteId}
        />
      )}

      {transfersModalOpen && (
        <PacientesTransferModal
        pacienteId={selectedPacienteIdForTransfers}
        open={transfersModalOpen}
        onClose={() => setTransfersModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Patients;
